"use client";

import { useState, useEffect } from "react";
import { usePathname } from "next/navigation";
import { Navbar } from "./navbar";
import { Sidebar } from "./sidebar";
import { ErrorBoundary } from "./error-boundary";
import { SkipLink } from "@/components/ui/accessibility";
import { cn } from "@/lib/utils";

interface AppLayoutProps {
  children: React.ReactNode;
}

const SIDEBAR_STORAGE_KEY = "sidebar-open-state";

export function AppLayout({ children }: Readonly<AppLayoutProps>) {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const pathname = usePathname();
  const fullWidthRoutes = ["/propostas"];
  const isFullWidthPage = pathname ? fullWidthRoutes.some((route) => pathname.startsWith(route)) : false;

  useEffect(() => {
    const savedState = localStorage.getItem(SIDEBAR_STORAGE_KEY);
    if (savedState !== null) {
      setSidebarOpen(savedState === "true");
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(SIDEBAR_STORAGE_KEY, String(sidebarOpen));
  }, [sidebarOpen]);

  const toggleSidebar = () => {
    setSidebarOpen((prev) => !prev);
  };

  return (
    <ErrorBoundary>
      <div className="relative min-h-screen" style={{ background: "var(--p-surface-0)" }}>
        <SkipLink targetId="main-content" label="Pular para o conteúdo principal" />
        <Navbar onMenuClick={toggleSidebar} sidebarOpen={sidebarOpen} />
        <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

        <main
          id="main-content"
          aria-label="Conteúdo principal"
          className={cn(
            "pt-16 overflow-auto transition-all duration-300",
            sidebarOpen ? "lg:pl-64" : "lg:pl-0"
          )}
        >
          <div
            className={cn(
              "px-4 py-6 md:px-6 md:py-8 lg:px-8 lg:py-10",
              isFullWidthPage ? "w-full" : "mx-auto max-w-[1400px]"
            )}
          >
            {children}
          </div>
        </main>
      </div>
    </ErrorBoundary>
  );
}

